import { CrossCircledIcon, MagnifyingGlassIcon, CheckCircledIcon } from "@radix-ui/react-icons";
import { z } from "zod";

import { TaskSource } from "@/src/lib/task-source";
import { Classification } from "@/src/models/constants/classifications";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { Section } from "@/src/models/section";

export enum FileType {
  READY = "ready", // TODO: remove VAL-300
  ORIGINAL = "original", // TODO: remove VAL-300
  INBOUND = "inbound",
  OUTBOUND = "outbound",
}

export enum ClassifyStatus {
  BATCH_CLASSIFY = "batch_classify",
  CLASSIFY = "classify",
  DUPLICATE = "duplicate",
  PROCESSED = "processed",
  FAILED = "failed",
}

export enum FilePriority {
  LOW = "Low",
  MEDIUM = "Medium",
  HIGH = "High",
}

export const statusIcons = {
  [ClassifyStatus.BATCH_CLASSIFY]: { icon: MagnifyingGlassIcon, color: "muted-foreground" },
  [ClassifyStatus.CLASSIFY]: { icon: MagnifyingGlassIcon, color: "muted-foreground" },
  [ClassifyStatus.DUPLICATE]: { icon: CrossCircledIcon, color: "orange-500" },
  [ClassifyStatus.PROCESSED]: { icon: CheckCircledIcon, color: "blue-500" },
  [ClassifyStatus.FAILED]: { icon: CrossCircledIcon, color: "red-500" },
} as const;

export const classificationConfidenceSchema = z.record(z.nativeEnum(Classification), z.number());

export const splitDocumentConfidenceSchema = z.record(
  z.nativeEnum(Classification),
  z.object({
    pages: z.array(z.number()),
    pageRanges: z.array(z.array(z.number())),
    confidence: z.number(),
    reasoning: z.string().nullable(),
  })
);

export type ClassificationConfidenceDict = z.infer<typeof classificationConfidenceSchema>;
export type SplitDocumentConfidenceDict = z.infer<typeof splitDocumentConfidenceSchema>;
export type ClassificationPageDict = Partial<
  Record<Classification, { pages: number[]; pageRanges: number[][]; groupedClassifications?: Classification[] }>
>;

export const fileSchema = z.object({
  pathToFile: z.string(),
  filename: z.string(),
  contentType: z.string(),
  checksum: z.string(),
  classification: z.nativeEnum(Classification).nullable(),
  type: z.nativeEnum(FileType),
  fileSize: z.number(),
  processedContent: z.any().nullable(),
  reviewedContent: z.any().nullable(),
  classificationConfidence: classificationConfidenceSchema.nullable(),
  splitDocumentConfidence: splitDocumentConfidenceSchema.nullable(),
  reviewedSplitDocumentConfidence: splitDocumentConfidenceSchema.nullable(),
  batchOperationName: z.string().nullable(),
  batchExtractId: z.string().nullable(),
  completedAt: z.string().nullable(),
  classifyStatus: z.nativeEnum(ClassifyStatus),
  displayId: z.string().nullable(),
  note: z.string().nullable(),
  priority: z.nativeEnum(FilePriority).default(FilePriority.LOW),
  ehrDocumentId: z.string().nullable(),
  ehrDocumentType: z.string().nullable(),
  source: z.nativeEnum(TaskSource).nullable(),
  pageHashes: z
    .array(z.object({ pageNumber: z.number(), hash: z.string() }))
    .nullable()
    .optional(),
  pathToDeduplicationImages: z.string().nullable().optional(),
  duplicatesFileId: z.string().uuid().nullable().optional(),
  hasMultiplePatients: z.boolean().default(false),
  croppedPageIndices: z.array(z.number().int().nonnegative()).nullable(),
});

export type File = z.infer<typeof fileSchema> & {
  sections: Section[];
} & DatabaseDefaults;

export const fileShape: File = {
  id: "",
  groupId: "",
  pathToFile: "",
  filename: "",
  contentType: "",
  checksum: "",
  type: FileType.READY,
  fileSize: 0,
  processedContent: null,
  reviewedContent: null,
  classification: null,
  classificationConfidence: null,
  splitDocumentConfidence: null,
  reviewedSplitDocumentConfidence: null,
  batchOperationName: null,
  batchExtractId: null,
  sections: [],
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
  completedAt: null,
  classifyStatus: ClassifyStatus.BATCH_CLASSIFY,
  displayId: null,
  note: null,
  priority: FilePriority.LOW,
  ehrDocumentId: null,
  ehrDocumentType: null,
  source: null,
  pageHashes: null,
  pathToDeduplicationImages: null,
  duplicatesFileId: null,
  hasMultiplePatients: false,
  croppedPageIndices: null,
};
